import { formatLabel } from '../utils/formatText';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { brands as mockBrands } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Marcas = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/brands`);
      if (response.data && response.data.length > 0) {
        setBrands(response.data);
      } else {
        setBrands(mockBrands);
      }
    } catch (error) {
      setBrands(mockBrands);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const trackView = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track/view?page=marcas`);
      } catch (e) {}
    };
    trackView();
  }, []);

  const featuredBrands = brands.filter(b => b.featured);
  const otherBrands = brands.filter(b => !b.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#EA1266] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="marcas-page">
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white py-20 md:py-28">
        <div className="container mx-auto px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-4 block">Nossas Parceiras</span>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight" data-testid="marcas-title">
            Nossas Marcas
          </h1>
          <p className="text-base text-gray-400 mt-4 font-medium max-w-lg">
            Trabalhamos apenas com as marcas mais confiáveis e reconhecidas do mercado
          </p>
        </div>
      </section>

      {/* Featured Brands */}
      {featuredBrands.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-3 block">Destaque</span>
              <h2 className="text-2xl sm:text-3xl font-medium text-[#0A0A0A] tracking-tight">Marcas em Destaque</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {featuredBrands.map((brand) => (
                <Card key={brand.id} className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(234,18,102,0.1)] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="bg-[#F3F4F6] rounded-2xl p-8 mb-6 flex items-center justify-center h-[100px]">
                      <img src={brand.logo} alt={brand.name} className="max-h-12 max-w-[160px] w-auto object-contain" />
                    </div>
                    <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">{formatLabel(brand.name)}</h3>
                    {brand.description && (
                      <p className="text-sm text-[#4B5563] mb-6 leading-relaxed font-medium line-clamp-3">{brand.description}</p>
                    )}
                    <Link to={`/catalogo?marca=${brand.name}`}>
                      <Button className="bg-[#EA1266] hover:bg-[#d00f58] text-white w-full rounded-full font-medium text-sm shadow-lg shadow-pink-500/20">
                        Ver Produtos {formatLabel(brand.name)}
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other Brands */}
      <section className={featuredBrands.length === 0 ? 'py-16 md:py-24' : 'pb-16 md:pb-24'}>
        <div className="container mx-auto px-6 md:px-12">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-medium text-[#0A0A0A] tracking-tight">
                {featuredBrands.length > 0 ? 'Todas as Marcas' : 'Nossas Marcas'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
              {(featuredBrands.length > 0 ? otherBrands : brands).map((brand) => (
                <Link key={brand.id} to={`/catalogo?marca=${brand.name}`} className="group" data-testid={`brand-link-${brand.name}`}>
                  <Card className="bg-white rounded-xl border border-gray-100 hover:shadow-[0_8px_30px_rgb(234,18,102,0.08)] transition-all duration-300 hover:-translate-y-0.5">
                    <CardContent className="p-4 md:p-6 flex flex-col items-center justify-center h-[110px] md:h-[130px]">
                      <div className="flex-1 flex items-center justify-center w-full">
                        <img src={brand.logo} alt={brand.name} className="max-h-8 md:max-h-10 max-w-[80px] md:max-w-[100px] w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300" />
                      </div>
                      <span className="mt-3 text-xs text-[#4B5563] group-hover:text-[#EA1266] transition-colors text-center font-medium">
                        {brand.name}
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#EA1266] text-white py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium mb-4 tracking-tight">
            Encontre Produtos de Todas as Marcas
          </h2>
          <p className="text-base mb-8 text-white/80 max-w-xl mx-auto font-medium">
            Confira nosso catálogo completo com produtos originais e qualidade garantida
          </p>
          <Link to="/catalogo">
            <Button className="bg-white text-[#EA1266] hover:bg-gray-100 rounded-full px-8 py-6 text-sm uppercase tracking-[0.2em] font-medium shadow-xl hover:scale-105 transition-all duration-300" data-testid="marcas-catalog-btn">
              Acessar Catálogo
              <ArrowRight className="ml-3" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
